import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { EvalOutputView, EvalRunDetail, SideResult } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { Badge, Button, Card, ProgressBar, Spinner } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { busyInterval } from '../../lib/busy';
import { blockLabel } from '../../lib/draft-editor';
import { errorText } from '../../lib/errors';
import { nextUnpicked, outputsSaved, progressOf } from '../../lib/eval-review';
import { formatCents, formatSeconds } from '../../lib/format';
import { kindLabel } from '../../lib/labels';

type Choice = 'first' | 'second' | 'tie';

/** One side as the owner sees it: never which version it is. */
function Variant({ view, format }: { view: EvalOutputView; format: 'reel' | 'carousel' }) {
  const t = useT();
  if (view.kind === 'pending') return <p className="text-sm text-hint">{t.adminEval.pendingVariant}</p>;
  if (view.kind === 'error') {
    return <p className="text-sm text-destructive break-words">{t.adminEval.failedVariant(view.message)}</p>;
  }
  if (view.kind === 'ideas') {
    return (
      <ul className="space-y-3">
        {view.ideas.map((idea, i) => (
          <li key={i}>
            <div className="flex flex-wrap gap-1 mb-1">
              <Badge tone="neutral">{idea.angle}</Badge>
              <Badge tone="ink">{kindLabel(idea.format, idea.type ?? 'scenes', t)}</Badge>
            </div>
            <p className="text-sm font-semibold break-words">{idea.hook}</p>
            <p className="text-sm break-words">{idea.summary}</p>
          </li>
        ))}
      </ul>
    );
  }
  const blocks = view.text.blocks;
  return (
    <ul className="space-y-2">
      {blocks.map((b, i) => (
        <li key={i}>
          <p className="text-xs uppercase tracking-wider text-hint">{blockLabel(format, i, blocks.length, t)}</p>
          {b.title && <p className="text-sm font-semibold break-words">{b.title}</p>}
          <p className="text-sm whitespace-pre-line break-words">{b.text}</p>
        </li>
      ))}
    </ul>
  );
}

function SideCard({ label, side }: { label: string; side: SideResult }) {
  const t = useT();
  const row = (name: string, value: string) => (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <span>{name}</span>
      <span className="tabular-nums font-medium">{value}</span>
    </div>
  );
  return (
    <Card variant="surface" padding="md">
      <p className="text-sm font-semibold">
        {label} · v{side.version}
      </p>
      <p className="text-xs text-hint mb-2 break-all">{side.model}</p>
      {row(t.adminEval.wins, String(side.wins))}
      {row(t.adminEval.failures, String(side.failures))}
      {row(t.adminEval.avgCost, side.avgUsd === null ? '—' : formatCents(side.avgUsd))}
      {row(t.adminEval.avgTime, side.avgMs === null ? '—' : formatSeconds(side.avgMs / 1000))}
    </Card>
  );
}

/** A check-set run: the progress while it runs, then the blind review and the result (spec 7.5). */
export function AdminEvalRunPage() {
  const t = useT();
  const qc = useQueryClient();
  const { toast, show } = useToast();
  const id = Number(useParams().id);
  const [reveal, setReveal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [picking, setPicking] = useState<Choice | null>(null);
  const key = ['admin', 'eval', 'run', id, reveal];

  const query = useQuery<EvalRunDetail>({
    queryKey: key,
    queryFn: () => api.adminEvalRun(id, reveal),
    enabled: Number.isInteger(id) && id > 0,
    refetchInterval: (q) => busyInterval('eval', q.state.data?.run.status),
  });
  const detail = query.data;

  const pick = async (itemId: number, choice: Choice) => {
    setPicking(choice);
    try {
      const next = await api.adminEvalPick(id, { itemId, choice });
      if (reveal) await qc.invalidateQueries({ queryKey: key });
      else qc.setQueryData(key, next);
      setExpanded(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'eval', 'runs'] });
    } catch (err) {
      show(errorText(err, t), 'destructive');
    } finally {
      setPicking(null);
    }
  };

  let body: JSX.Element;
  if (query.error) {
    body = <p className="text-sm text-hint">{errorText(query.error, t)}</p>;
  } else if (!detail) {
    body = (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  } else if (detail.run.status === 'running') {
    const { saved, total } = outputsSaved(detail);
    body = (
      <Card variant="surface" padding="lg" className="text-center">
        <Spinner className="mb-3" />
        <p className="text-sm mb-3">{t.adminEval.running(saved, total)}</p>
        <ProgressBar value={total ? (saved / total) * 100 : 0} />
      </Card>
    );
  } else {
    const format = detail.run.code === 'text_carousel' ? 'carousel' : 'reel';
    const index = nextUnpicked(detail.items);
    const item = index === null ? null : detail.items[index]!;
    const { total } = progressOf(detail.items);
    body = (
      <>
        {detail.result && (
          <section className="mb-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-hint mb-2">{t.adminEval.result}</h2>
            <div className="space-y-2 mb-2">
              <SideCard label={t.adminEval.versionA} side={detail.result.a} />
              <SideCard label={t.adminEval.versionB} side={detail.result.b} />
            </div>
            <p className="text-sm text-hint">{t.adminEval.ties(detail.result.ties)}</p>
          </section>
        )}

        {item && (
          <section key={item.itemId}>
            <p className="text-xs text-hint mb-1">{t.adminEval.progress(index! + 1, total)}</p>
            <h2 className="text-base font-semibold mb-2 break-words">{item.title}</h2>
            <Card variant="surface" padding="md" className="mb-3">
              <p className={expanded ? 'text-sm whitespace-pre-line break-words' : 'text-sm line-clamp-4 break-words'}>
                {item.transcript}
              </p>
              {!expanded && (
                <button type="button" className="text-xs text-fg mt-1" onClick={() => setExpanded(true)}>
                  {t.thought.showAll}
                </button>
              )}
            </Card>
            {item.idea && (
              <Card variant="outline" padding="md" className="mb-3">
                <p className="text-xs uppercase tracking-wider text-hint mb-1">{t.adminEval.idea}</p>
                <p className="text-sm font-semibold break-words">{item.idea.hook}</p>
                <p className="text-sm break-words">{item.idea.summary}</p>
              </Card>
            )}
            {([1, 2] as const).map((n) => (
              <Card key={n} variant="surface" padding="md" className="mb-3">
                <p className="text-sm font-semibold mb-2">{t.adminEval.variant(n)}</p>
                <Variant view={n === 1 ? item.first : item.second} format={format} />
              </Card>
            ))}
            <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-4 bg-canvas/95 safe-bottom grid grid-cols-3 gap-2">
              <Button variant="ink" loading={picking === 'first'} disabled={picking !== null} onClick={() => pick(item.itemId, 'first')}>
                {t.adminEval.better(1)}
              </Button>
              <Button variant="secondary" loading={picking === 'tie'} disabled={picking !== null} onClick={() => pick(item.itemId, 'tie')}>
                {t.adminEval.tie}
              </Button>
              <Button variant="ink" loading={picking === 'second'} disabled={picking !== null} onClick={() => pick(item.itemId, 'second')}>
                {t.adminEval.better(2)}
              </Button>
            </div>
            {!detail.result && (
              <Button variant="ghost" size="sm" fullWidth className="mt-2" onClick={() => setReveal(true)}>
                {t.adminEval.reveal}
              </Button>
            )}
          </section>
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.adminEval.title} to="/admin/eval" />
      {body}
      {toast}
    </div>
  );
}
